import Fuse from "fuse.js";
import {
  FUZZY_MATCH_OPTIONS,
  MAX_FUZZY_QUERY_LENGTH,
  digitsAreConsistent
} from "utils/fuzzySearch";

import { IGroupByListColumn } from "models";

import { MAX_SEARCH_RESULTS } from "../group-by.context";
import { fieldGroupKey } from "./groupFields";

/**
 * Returns the columns whose title contains the trimmed `searchString`, or — only
 * when none does — the columns whose title fuzzy-matches it, capped at
 * MAX_SEARCH_RESULTS. Mixing typo matches in alongside exact ones reads as
 * unrelated results once they are grouped rather than ranked below the exact
 * hits.
 *
 * Relevance from a single Fuse pass over the whole list decides which columns
 * make the cap, so a weaker match early in `list` can't take a slot from a
 * closer one later in it. The picker
 * renders one block per fieldGroupKey in first-appearance order, so the capped
 * results are re-ordered by each block's earliest surviving position in `list`
 * (browse order). Relevance order is kept within a block here, though
 * FieldList re-sorts a block by `withinSubgroupOrder` before rendering.
 */
const filterByText = (
  searchString: string,
  list: IGroupByListColumn[]
): IGroupByListColumn[] => {
  const query = searchString.trim();
  if (query.length === 0) {
    return list;
  }

  const q = query.toLowerCase();

  if (query.length > MAX_FUZZY_QUERY_LENGTH) {
    return list
      .filter((column) => column.title.toLowerCase().includes(q))
      .slice(0, MAX_SEARCH_RESULTS);
  }

  const fuse = new Fuse(list, { ...FUZZY_MATCH_OPTIONS, keys: ["title"] });

  const matches = fuse
    .search(query)
    .filter((result) => digitsAreConsistent(result.item.title, query));
  const exactMatches = matches.filter((result) =>
    result.item.title.toLowerCase().includes(q)
  );
  const results = (exactMatches.length ? exactMatches : matches).slice(
    0,
    MAX_SEARCH_RESULTS
  );

  const blockRank = new Map<string, number>();
  for (const { item, refIndex } of results) {
    const key = fieldGroupKey(item);
    blockRank.set(key, Math.min(blockRank.get(key) ?? refIndex, refIndex));
  }
  const rankOf = (item: IGroupByListColumn): number => blockRank.get(fieldGroupKey(item));

  return results
    .sort((a, b) => rankOf(a.item) - rankOf(b.item))
    .map((result) => result.item);
};

export default filterByText;
